import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { plaqueStatusLabel } from '../lib/plaqueStatus';
import { toast } from 'sonner';
import { TaxFieldsForm, MaskedTaxSummary } from '../components/TaxFieldsForm';
import { AgreementsPanel } from '../components/AgreementsPanel';

function money(usd) {
  if (usd === null || usd === undefined) return '$0.00';
  return `$${Number(usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function shortDate(iso) { return iso ? iso.slice(0, 10) : '—'; }

export default function AdminAccountDetail() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/accounts/${userId}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/accounts/${userId}`);
        if (alive) setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <AdminLayout>
          <div className="text-[hsl(var(--primary)/0.6)]">{loading ? 'Loading…' : 'Account not found.'}</div>
        </AdminLayout>
      </div>
    );
  }

  const u = data.user || {};
  const at = u.account_type || 'family';

  const handleDeleteFamily = async () => {
    if (at !== 'family') {
      toast.error('Only family accounts can be deleted here.');
      return;
    }
    const typed = window.prompt(
      `This will permanently delete ${u.email}'s account AND all their memorials, tributes, QR/plaque orders, and payment history.\n\nThis cannot be undone.\n\nType the email exactly to confirm:`,
      '',
    );
    if (typed === null) return;
    if ((typed || '').trim().toLowerCase() !== (u.email || '').trim().toLowerCase()) {
      toast.error('Email did not match. Deletion cancelled.');
      return;
    }
    if (!window.confirm(`Final confirmation — delete ${u.email}? This is irreversible.`)) return;
    setDeleting(true);
    try {
      const { data: resp } = await api.delete(
        `/admin/accounts/${userId}?confirm_email=${encodeURIComponent(u.email)}`,
      );
      const counts = resp?.deleted_counts || {};
      toast.success(`Account deleted (${counts.memorials || 0} memorials, ${counts.tributes || 0} tributes, ${counts.payment_transactions || 0} payments).`);
      nav('/admin/accounts');
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link to="/admin/accounts" className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))] hover:underline" data-testid="admin-account-back">
              ← All accounts
            </Link>
            <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2" data-testid="admin-account-detail-title">
              {u.name || u.email || u.user_id}
            </h1>
            <div className="text-sm text-[hsl(var(--primary)/0.7)] mt-1">
              {at.replace('_', ' ')} · {u.email || '—'}
            </div>
          </div>
          <span className="text-xs uppercase px-3 py-1 rounded-full border border-[hsl(var(--primary)/0.18)]" data-testid="admin-account-status-pill">
            {u.partner_status || u.account_status || (at === 'family' ? 'active' : '—')}
          </span>
        </header>

        <Section title="Account">
          <Grid>
            <KV label="User ID"        value={u.user_id} mono />
            <KV label="Account type"   value={at} />
            <KV label="Email"          value={u.email} />
            <KV label="Phone"          value={u.phone} />
            <KV label="First name"     value={u.first_name} />
            <KV label="Last name"      value={u.last_name} />
            <KV label="Address"        value={u.address_line1} />
            <KV label="City / State / ZIP" value={u.city ? `${u.city}, ${u.state || '—'} ${u.postal_code || ''}`.trim() : '—'} />
            <KV label="Country"        value={u.country} />
            <KV label="Profile filled" value={u.profile_completed ? 'Yes' : 'No'} />
            <KV label="Auth provider"  value={u.google_oauth_sub ? 'Google' : 'Email + password'} />
            <KV label="Created"        value={shortDate(u.created_at)} />
            <KV label="Plan"           value={u.plan} />
            <KV label="Role"           value={u.role} />
            <KV label="Last activity"  value={shortDate(data?.family?.last_login_at || u.last_login_at)} />
          </Grid>
        </Section>

        {at === 'family' && data.family && <FamilyDetail family={data.family} />}
        {at === 'regional_partner' && data.partner && <RegionalDetail partner={data.partner} user={u} />}
        {at === 'memorial_partner' && data.partner && <MemorialDetail partner={data.partner} user={u} />}

        <AdminToolsPanel user={u} onReload={reload} />

        {(at === 'regional_partner' || at === 'memorial_partner') && (
          <AgreementsPanel user={u} />
        )}

        {(at === 'regional_partner' || at === 'memorial_partner') && (
          <AdminTaxFieldsPanel user={u} onReload={reload} />
        )}

        {at === 'family' && (
          <section className="mt-10 mb-6 rounded-2xl border border-red-300/60 bg-red-50/30 p-6" data-testid="admin-account-danger-zone">
            <p className="ml-overline text-red-700">Danger zone</p>
            <h3 className="font-serif text-2xl text-red-900 mt-1">Delete this account</h3>
            <p className="text-sm text-red-900/80 mt-2 max-w-xl">
              Permanently removes <strong>{u.email}</strong>, all their owned memorials, tributes, QR / plaque orders, engraving requests, payments, and notifications. This action cannot be undone and will be recorded in the audit log.
            </p>
            <button
              type="button"
              onClick={handleDeleteFamily}
              disabled={deleting}
              className="mt-5 px-5 py-2.5 rounded-full border border-red-600 bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition disabled:opacity-60"
              data-testid="admin-account-delete-btn"
            >
              {deleting ? 'Deleting…' : `Delete ${u.first_name || u.email} permanently`}
            </button>
          </section>
        )}
      </AdminLayout>
    </div>
  );
}

function FamilyDetail({ family }) {
  return (
    <>
      <Section title="Memorials owned" testid="admin-account-family-memorials">
        {family.memorials?.length === 0 ? <Empty>No memorials yet.</Empty> : (
          <Table headers={['Name', 'Slug', 'QR mode', 'Created', '']}>
            {family.memorials.map((m) => (
              <tr key={m.memorial_id} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3">{m.full_name}</td>
                <td className="py-2 px-3 font-mono text-xs">{m.slug}</td>
                <td className="py-2 px-3">{m.qr_access_mode || 'public'}</td>
                <td className="py-2 px-3">{shortDate(m.created_at)}</td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  <Link
                    to={`/dashboard/m/${m.memorial_id}?support=1`}
                    className="text-[hsl(var(--accent))] hover:underline text-xs uppercase tracking-[0.16em] mr-4"
                    data-testid={`admin-account-view-as-owner-${m.memorial_id}`}
                    title="Open exactly as the account owner sees it (Admin Support Mode)"
                  >
                    View as Account Owner →
                  </Link>
                  <Link
                    to={`/admin/memorials/${m.memorial_id}`}
                    className="text-[hsl(var(--accent))] hover:underline text-xs uppercase tracking-[0.16em]"
                    data-testid={`admin-account-open-memorial-${m.memorial_id}`}
                  >
                    Open dossier →
                  </Link>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Payments" testid="admin-account-family-payments">
        {family.payments?.length === 0 ? <Empty>No payments yet.</Empty> : (
          <Table headers={['Plan', 'Cycle', 'Amount', 'Status', 'Date']}>
            {family.payments.slice(0, 25).map((p) => (
              <tr key={p.transaction_id} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3">{p.plan_id}</td>
                <td className="py-2 px-3">{p.billing_cycle}</td>
                <td className="py-2 px-3">{money(p.amount)}</td>
                <td className="py-2 px-3">{p.payment_status}</td>
                <td className="py-2 px-3">{shortDate(p.created_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Plaque orders" testid="admin-account-family-plaque-orders">
        {family.plaque_orders?.length === 0 ? <Empty>No plaque orders.</Empty> : (
          <Table headers={['Order', 'Product', 'Qty', 'Amount', 'Status', 'Paid']}>
            {family.plaque_orders.map((o) => (
              <tr key={o.order_id} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3 font-mono text-xs">
                  <Link
                    to={`/admin/plaque-orders?focus=${o.order_id}`}
                    className="text-[hsl(var(--accent))] hover:underline"
                    data-testid={`admin-account-plaque-order-${o.order_id}`}
                  >
                    {o.order_id.replace('plaque_', '')}
                  </Link>
                </td>
                <td className="py-2 px-3">{o.product_label}</td>
                <td className="py-2 px-3">{o.quantity}</td>
                <td className="py-2 px-3">{money(o.amount_usd)}</td>
                <td className="py-2 px-3">{plaqueStatusLabel(o.status)}</td>
                <td className="py-2 px-3">{shortDate(o.paid_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="QR orders" testid="admin-account-family-qr-orders">
        {family.qr_orders?.length === 0 ? <Empty>No QR orders.</Empty> : (
          <Table headers={['Order', 'Type', 'Qty', 'Status', 'Date']}>
            {family.qr_orders.map((o) => (
              <tr key={o.order_id} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3 font-mono text-xs">{o.order_id.replace('ord_', '')}</td>
                <td className="py-2 px-3">{o.product_label || o.order_type}</td>
                <td className="py-2 px-3">{o.quantity}</td>
                <td className="py-2 px-3">{o.status}</td>
                <td className="py-2 px-3">{shortDate(o.created_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Engraving quote requests" testid="admin-account-family-engraving">
        {family.engraving_quote_requests?.length === 0 ? <Empty>No engraving requests.</Empty> : (
          <Table headers={['Request', 'Deceased', 'Cemetery', 'Status', 'Created']}>
            {family.engraving_quote_requests.map((r) => (
              <tr key={r.request_id} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3 font-mono text-xs">{r.request_id.replace('engreq_', '')}</td>
                <td className="py-2 px-3">{r.deceased_full_name}</td>
                <td className="py-2 px-3">{r.cemetery_name}</td>
                <td className="py-2 px-3">{r.status}</td>
                <td className="py-2 px-3">{shortDate(r.created_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Tribute submissions" testid="admin-account-family-tributes">
        {family.tributes?.length === 0 ? <Empty>No tributes.</Empty> : (
          <Table headers={['From', 'Status', 'Created']}>
            {family.tributes.slice(0, 25).map((t) => (
              <tr key={t.tribute_id || `${t.memorial_id}-${t.created_at}`} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3">{t.name || '—'}</td>
                <td className="py-2 px-3">{t.status}</td>
                <td className="py-2 px-3">{shortDate(t.created_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}

function PartnerKVBase({ user, partner }) {
  const sc = partner.stripe_connect || {};
  const scStatus = sc.payouts_enabled ? 'Active' : (sc.details_submitted ? 'Verification pending' : 'Not connected');
  return (
    <Section title="Partner profile" testid="admin-account-partner-profile">
      <Grid>
        <KV label="Business name"        value={user.business_name} />
        <KV label="Legal business name"  value={user.legal_business_name} />
        <KV label="Partner type"         value={user.partner_type} />
        <KV label="Tax ID (last 4)"      value={user.tax_id_last4} mono />
        <KV label="Referral / invite code" value={partner.referral_code} mono />
        <KV label="Status"               value={user.partner_status} />
        <KV label="W-9 uploaded"         value={user.w9_uploaded_at ? shortDate(user.w9_uploaded_at) : 'No'} />
        <KV label="W-9 filename"         value={partner.w9_filename || '—'} />
        <KV label="Stripe Connect"       value={scStatus} />
        <KV label="Charges enabled"      value={sc.charges_enabled ? 'Yes' : 'No'} />
        <KV label="Agreement version"    value={user.agreement_version} />
        <KV label="Agreement signed at"  value={shortDate(user.agreement_accepted_at)} />
      </Grid>
    </Section>
  );
}

function RegionalDetail({ user, partner }) {
  return (
    <>
      <PartnerKVBase user={user} partner={partner} />
      <Section title="Performance" testid="admin-account-regional-perf">
        <Grid>
          <KV label="Direct families"       value={partner.direct_family_count} />
          <KV label="Memorial Partners"     value={partner.members_count} />
          <KV label="Indirect families"     value={partner.indirect_family_count} />
          <KV label="Total families"        value={partner.total_family_count} />
          <KV label="Commissions paid"      value={money(partner.commissions_paid_usd)} />
          <KV label="Commissions pending"   value={money(partner.commissions_pending_usd)} />
          <KV label="Commissions reversed"  value={money(partner.commissions_reversed_usd)} />
        </Grid>
      </Section>
      <Section title="Memorial Partners" testid="admin-account-regional-members">
        {partner.members?.length === 0 ? <Empty>No Memorial Partners recruited yet.</Empty> : (
          <Table headers={['Business', 'Status', 'Override status', 'Expires', 'Joined']}>
            {partner.members.map((m) => (
              <tr key={m.user_id} className="border-t border-[hsl(var(--primary)/0.08)]">
                <td className="py-2 px-3">
                  <Link to={`/admin/accounts/${m.user_id}`} className="text-[hsl(var(--accent))] hover:underline">{m.business_name || m.name || m.user_id}</Link>
                </td>
                <td className="py-2 px-3">{m.partner_status}</td>
                <td className="py-2 px-3">{m.override_status || '—'}</td>
                <td className="py-2 px-3">{shortDate(m.override_expiration_date)}</td>
                <td className="py-2 px-3">{shortDate(m.created_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
      <Section title="Signed agreement history" testid="admin-account-agreement-history">
        <AgreementHistory entries={partner.agreement_history} />
      </Section>
    </>
  );
}

function MemorialDetail({ user, partner }) {
  return (
    <>
      <PartnerKVBase user={user} partner={partner} />
      <Section title="Assigned Regional Partner" testid="admin-account-mp-rp">
        {!partner.assigned_regional_partner ? <Empty>Not currently assigned.</Empty> : (
          <Grid>
            <KV label="Business name" value={partner.assigned_regional_partner.business_name || partner.assigned_regional_partner.name} />
            <KV label="Contact email" value={partner.assigned_regional_partner.email} />
            <KV label="Referral code" value={partner.assigned_regional_partner.partner_invite_code} mono />
          </Grid>
        )}
      </Section>
      <Section title="Override contract" testid="admin-account-mp-override">
        <Grid>
          <KV label="Status"            value={partner.override?.override_status} />
          <KV label="Start"             value={shortDate(partner.override?.override_start_date)} />
          <KV label="Expiration"        value={shortDate(partner.override?.override_expiration_date)} />
        </Grid>
      </Section>
      <Section title="Performance" testid="admin-account-mp-perf">
        <Grid>
          <KV label="Direct families"      value={partner.direct_family_count} />
          <KV label="Commissions paid"     value={money(partner.commissions_paid_usd)} />
          <KV label="Commissions pending"  value={money(partner.commissions_pending_usd)} />
          <KV label="Commissions reversed" value={money(partner.commissions_reversed_usd)} />
        </Grid>
      </Section>
      <Section title="Signed agreement history" testid="admin-account-agreement-history">
        <AgreementHistory entries={partner.agreement_history} />
      </Section>
    </>
  );
}

function AgreementHistory({ entries }) {
  if (!entries || entries.length === 0) {
    return <Empty>No signed agreement on file.</Empty>;
  }
  return (
    <Table headers={['Version', 'Event', 'When', 'SHA-256']}>
      {entries.map((h, idx) => (
        <tr key={`agr-${idx}`} className="border-t border-[hsl(var(--primary)/0.08)]">
          <td className="py-2 px-3">{h.version}</td>
          <td className="py-2 px-3">{h.event}</td>
          <td className="py-2 px-3">{shortDate(h.resent_at || h.approved_at)}</td>
          <td className="py-2 px-3 font-mono text-[10px] text-[hsl(var(--primary)/0.55)] truncate max-w-[200px]">{h.sha256}</td>
        </tr>
      ))}
    </Table>
  );
}

// ============================================================================
// AdminToolsPanel — Edit account / Terminate-Suspend partner / Resend agreement
// ============================================================================
const EDITABLE_FIELDS_DEF = [
  { key: 'name',             label: 'Name' },
  { key: 'business_name',    label: 'Business name' },
  { key: 'email',            label: 'Email' },
  { key: 'phone',            label: 'Phone' },
  { key: 'address_street',   label: 'Street' },
  { key: 'address_city',     label: 'City' },
  { key: 'address_state',    label: 'State' },
  { key: 'address_postal',   label: 'Postal code' },
  { key: 'address_country',  label: 'Country' },
  { key: 'plan',             label: 'Plan' },
  { key: 'tax_id_last4',     label: 'Tax ID (last 4)' },
  { key: 'partner_invite_code', label: 'Invite / referral code' },
  { key: 'business_type',    label: 'Business type' },
  { key: 'partner_type',     label: 'Partner type' },
];

function AdminToolsPanel({ user, onReload }) {
  const [editOpen, setEditOpen]   = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const at = user.account_type || 'family';
  const isPartner = at === 'regional_partner' || at === 'memorial_partner';

  const handleResendAgreement = async () => {
    if (!isPartner) {
      toast.error('Resend agreement is only for partner accounts.');
      return;
    }
    const note = window.prompt(
      `Resend Partner Agreement to ${user.email}?\n\nOptional note shown to the partner in the email:`,
      '',
    );
    if (note === null) return;
    if (!window.confirm(`Final confirm — send the re-sign request email to ${user.email}?`)) return;
    try {
      const { data } = await api.post(
        `/admin/partners/${user.user_id}/agreement/resend`,
        { note: note || null },
      );
      if (data.email_sent) {
        toast.success(`Email queued — re-sign link sent to ${user.email}.`);
      } else {
        toast.warning('Email provider not configured. Copy the link below to send manually.');
      }
      if (data.signing_url) {
        try { await navigator.clipboard.writeText(data.signing_url); toast.info('Signing link copied to clipboard.'); }
        catch { /* ignore clipboard failures */ }
      }
      await onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <section className="mb-8 rounded-2xl border border-[hsl(var(--accent)/0.4)] bg-[hsl(var(--accent)/0.04)] p-6" data-testid="admin-tools-panel">
      <p className="ml-overline text-[hsl(var(--accent))]">Admin tools</p>
      <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">Account maintenance</h3>
      <p className="text-sm text-[hsl(var(--primary)/0.75)] mt-2 max-w-2xl">
        Every action below is recorded in the audit log with the admin's user_id and a timestamp.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          className="px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium hover:opacity-90 transition"
          data-testid="admin-tools-edit-btn"
        >
          Edit account details
        </button>
        <button
          type="button"
          onClick={async () => {
            const proceed = window.confirm(
              `Generate a fresh temporary password for ${user.email}?\n\n` +
              `The current password will be invalidated immediately. The new password is shown to you ONCE so you can share it with the customer (call / SMS / secure note). They should change it on first login.`
            );
            if (!proceed) return;
            try {
              const { data } = await api.post(`/admin/accounts/${user.user_id}/reset-password`);
              setTempPassword(data.temp_password);
              toast.success('Temporary password generated. Share it once and securely.');
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
          className="px-5 py-2.5 rounded-full border border-[hsl(var(--accent))] text-[hsl(var(--primary))] text-sm font-medium hover:bg-[hsl(var(--accent)/0.12)] transition"
          data-testid="admin-tools-reset-password-btn"
        >
          Reset password
        </button>
        {isPartner && (
          <>
            <button
              type="button"
              onClick={() => setStatusOpen(true)}
              className="px-5 py-2.5 rounded-full border border-[hsl(var(--destructive,0_70%_40%))] text-[hsl(var(--destructive,0_70%_40%))] text-sm font-medium hover:bg-[hsl(var(--destructive,0_70%_40%)/0.08)] transition"
              data-testid="admin-tools-status-btn"
            >
              Terminate / Suspend partner
            </button>
            <button
              type="button"
              onClick={handleResendAgreement}
              className="px-5 py-2.5 rounded-full border border-[hsl(var(--accent))] text-[hsl(var(--primary))] text-sm font-medium hover:bg-[hsl(var(--accent)/0.12)] transition"
              data-testid="admin-tools-resend-agreement-btn"
            >
              Resend partner agreement
            </button>
            <CustomAgreementButton user={user} onSaved={onReload} />
            <button
              type="button"
              onClick={async () => {
                const proceed = window.confirm(
                  `Delete this partner account so they can re-apply?\n\n` +
                  `Email: ${user.email}\n` +
                  `Name: ${user.name || '—'}\n\n` +
                  `This will hard-delete the application + invites, anonymise any signed agreements / commissions / payout history, and remove the user record. Once deleted, the same email can submit a fresh application.\n\n` +
                  `This action is irreversible.`,
                );
                if (!proceed) return;
                const confirmEmail = window.prompt(
                  `Type the partner's email EXACTLY to confirm:\n\n${user.email}`,
                  '',
                );
                if (!confirmEmail || confirmEmail.trim().toLowerCase() !== (user.email || '').toLowerCase()) return;
                try {
                  const { data } = await api.delete(`/admin/partners/${user.user_id}`, { params: { confirm_email: confirmEmail.trim() } });
                  toast.success(`Partner account deleted. Counts: ${JSON.stringify(data.deleted_counts)}`);
                  window.location.href = '/admin/accounts';
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              className="px-5 py-2.5 rounded-full border-2 border-red-600 text-red-700 text-sm font-medium hover:bg-red-50 transition"
              data-testid="admin-tools-delete-partner-btn"
            >
              Delete partner account (allow re-apply)
            </button>
          </>
        )}
      </div>
      {tempPassword && (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="admin-tools-temp-password">
          <div className="text-sm text-amber-900">
            Temporary password for <span className="font-mono">{user.email}</span>:&nbsp;
            <span className="font-mono text-lg select-all" data-testid="admin-tools-temp-password-value">{tempPassword}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={async () => {
                try { await navigator.clipboard.writeText(tempPassword); toast.success('Copied.'); }
                catch { toast.error('Could not copy. Select and copy manually.'); }
              }}
              className="text-xs uppercase tracking-[0.16em] text-amber-900 hover:underline"
              data-testid="admin-tools-temp-password-copy"
            >
              Copy
            </button>
            <button
              type="button"
              onClick={() => setTempPassword(null)}
              className="text-xs uppercase tracking-[0.16em] text-amber-900/70 hover:underline"
              data-testid="admin-tools-temp-password-dismiss"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {editOpen && (
        <EditAccountModal
          user={user}
          onClose={() => setEditOpen(false)}
          onSaved={async () => { setEditOpen(false); await onReload(); }}
        />
      )}
      {statusOpen && (
        <TerminateSuspendModal
          user={user}
          onClose={() => setStatusOpen(false)}
          onSaved={async () => { setStatusOpen(false); await onReload(); }}
        />
      )}
    </section>
  );
}

// ============================================================================
// CustomAgreementButton — iter97.23. Apply / clear a bespoke agreement
// preset (e.g. Jeffery Jay Bleim's custom compensation clauses) so the
// downloaded PDF and the next resend-email carry the amended text.
// ============================================================================
function CustomAgreementButton({ user, onSaved }) {
  const [open, setOpen]       = useState(false);
  const [presets, setPresets] = useState([]);
  const [saving, setSaving]   = useState(false);
  const currentKey = user.custom_agreement_body_key || '';

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get('/admin/agreement-presets');
        setPresets(data.presets || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [open]);

  const apply = async (key) => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/admin/partners/${user.user_id}/custom-agreement`,
        { preset_key: key || null },
      );
      toast.success(
        key
          ? `Applied: ${data.preset_label}. Re-download the PDF to see the new terms.`
          : 'Reverted to the standard partner agreement.'
      );
      setOpen(false);
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="px-5 py-2.5 rounded-full border border-[hsl(var(--accent))] text-[hsl(var(--primary))] text-sm font-medium hover:bg-[hsl(var(--accent)/0.12)] transition"
        data-testid="admin-tools-custom-agreement-btn"
      >
        Custom agreement…{currentKey && ' ✓'}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-tools-custom-agreement-modal">
          <div className="bg-[hsl(var(--card))] rounded-2xl max-w-lg w-full p-6 my-8 border border-[hsl(var(--primary)/0.18)] shadow-xl">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="ml-overline text-[hsl(var(--accent))]">Custom amendment</p>
                <h3 className="font-serif text-2xl text-[hsl(var(--primary))]">Apply a bespoke agreement</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] text-sm" data-testid="admin-tools-custom-agreement-close">
                Close ✕
              </button>
            </div>
            <p className="text-sm text-[hsl(var(--primary)/0.75)] mb-4">
              Currently applied: <span className="font-mono text-xs">{currentKey || 'standard (v1.1)'}</span>
              <br />
              Selecting a preset changes what appears in this partner&apos;s downloadable PDF and the next &quot;Resend partner agreement&quot; email. Every action is audit-logged.
            </p>
            <div className="space-y-2 mb-4">
              {presets.length === 0 ? (
                <div className="text-xs text-[hsl(var(--primary)/0.55)]">Loading presets…</div>
              ) : presets.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  disabled={saving || currentKey === p.key}
                  onClick={() => apply(p.key)}
                  className={
                    'w-full text-left rounded-xl border px-4 py-3 transition ' +
                    (currentKey === p.key
                      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.10)] cursor-default'
                      : 'border-[hsl(var(--primary)/0.15)] hover:bg-[hsl(var(--primary)/0.03)]')
                  }
                  data-testid={`admin-tools-custom-agreement-preset-${p.key}`}
                >
                  <div className="font-medium text-[hsl(var(--primary))] text-sm">{p.label}</div>
                  <div className="text-[11px] text-[hsl(var(--primary)/0.55)] font-mono mt-0.5">
                    key: {p.key} · {p.clause_count} clauses
                    {currentKey === p.key && <span className="ml-2 text-[hsl(var(--accent))]">· ACTIVE</span>}
                  </div>
                </button>
              ))}
            </div>
            {currentKey && (
              <button
                type="button"
                disabled={saving}
                onClick={() => apply(null)}
                className="text-xs uppercase tracking-[0.16em] text-red-700 hover:underline disabled:opacity-60"
                data-testid="admin-tools-custom-agreement-clear"
              >
                Revert to standard v1.1
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}


// ============================================================================
// AdminTaxFieldsPanel — admin view + edit of multi-country tax fields
// ============================================================================
function AdminTaxFieldsPanel({ user, onReload }) {
  const [masked, setMasked] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const loadMasked = React.useCallback(async () => {
    try {
      // Admin shortcut: route the GET through the admin masked-account
      // detail (already returned in `user`) — we project the same fields
      // server-side under `masked_tax_view`. To keep the component simple
      // here we just derive the masked view from the user row, matching
      // the shape `masked_tax_view()` returns on the backend.
      const v = {
        tax_country: user.tax_country || 'US',
        tax_country_label: ({ US: 'United States', CA: 'Canada', OTHER: 'Other / International' })[user.tax_country || 'US'],
        tax_contractor_type: user.tax_contractor_type || null,
        tax_contractor_type_label: ({ individual: 'Individual / Sole Proprietor', business: 'Business / LLC', corporation: 'Corporation' })[user.tax_contractor_type || ''] || null,
        legal_business_name: user.legal_business_name || null,
        tax_id_last4: user.tax_id_last4 || null,
        tax_id_masked: user.tax_id_last4 ? `•••• •••• ${user.tax_id_last4}` : null,
        has_tax_id: !!user.tax_id_last4 || !!user.has_tax_id,
        tax_sin_last3: user.tax_sin_last3 || null,
        tax_sin_masked: user.tax_sin_last3 ? `••• ••• ${user.tax_sin_last3}` : null,
        tax_business_number: user.tax_business_number || null,
        tax_corporation_name: user.tax_corporation_name || null,
        tax_foreign_id_last4: user.tax_foreign_id_last4 || null,
        tax_foreign_id_masked: user.tax_foreign_id_last4 ? `•••• •••• ${user.tax_foreign_id_last4}` : null,
        has_foreign_tax_id: !!user.tax_foreign_id_last4,
        tax_business_registration: user.tax_business_registration || null,
        tax_fields_updated_at: user.tax_fields_updated_at || null,
      };
      setMasked(v);
      setForm({
        tax_country: v.tax_country,
        tax_contractor_type: v.tax_contractor_type || '',
        legal_business_name: v.legal_business_name || '',
        tax_business_number: v.tax_business_number || '',
        tax_corporation_name: v.tax_corporation_name || '',
        tax_business_registration: v.tax_business_registration || '',
        // Sensitive fields are never pre-populated — admin must re-enter
        // to change them, leave blank to keep current.
        tax_id: '', tax_sin: '', tax_foreign_id: '',
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [user]);

  useEffect(() => { loadMasked(); }, [loadMasked]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send fields that have a meaningful value; blank sensitive
      // fields are dropped so the backend doesn't overwrite the
      // existing encrypted blob with `null`.
      const payload = {};
      for (const k of ['tax_country', 'tax_contractor_type', 'legal_business_name',
                       'tax_business_number', 'tax_corporation_name',
                       'tax_business_registration']) {
        const v = (form[k] || '').toString().trim();
        if (v) payload[k] = v;
      }
      for (const k of ['tax_id', 'tax_sin', 'tax_foreign_id']) {
        if ((form[k] || '').trim()) payload[k] = form[k].trim();
      }
      const { data } = await api.patch(`/admin/accounts/${user.user_id}/tax-fields`, payload);
      if (data?.no_changes) toast.info('No changes to apply.');
      else toast.success(`Saved ${data.changed_count || 0} field change(s).`);
      setEditing(false);
      await onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-8 rounded-2xl border border-[hsl(var(--primary)/0.14)] bg-[hsl(var(--card))] p-6" data-testid="admin-tax-fields-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="ml-overline text-[hsl(var(--accent))]">Tax fields</p>
          <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">Partner tax information</h3>
          <p className="text-xs text-[hsl(var(--primary)/0.7)] mt-2 max-w-2xl">
            Sensitive IDs are masked. Edits are recorded in the audit log under <span className="font-mono">tax.fields.updated</span> — sensitive values appear as <span className="font-mono">&lt;present&gt;</span> only.
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium hover:opacity-90 transition"
            data-testid="admin-tax-edit-btn"
          >
            Edit tax fields
          </button>
        )}
      </div>

      {!editing && masked && <MaskedTaxSummary masked={masked} />}

      {editing && (
        <div className="mt-5 space-y-5">
          <TaxFieldsForm value={form} onChange={setForm} masked={masked} mode="edit" />
          <div className="flex justify-end gap-3 pt-2 border-t border-[hsl(var(--primary)/0.08)]">
            <button
              type="button"
              onClick={() => { setEditing(false); loadMasked(); }}
              className="px-5 py-2.5 rounded-full border border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary))] text-sm"
              data-testid="admin-tax-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium hover:opacity-90 transition disabled:opacity-60"
              data-testid="admin-tax-save-btn"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}


function EditAccountModal({ user, onClose, onSaved }) {
  const initial = React.useMemo(() => {
    const o = {};
    for (const f of EDITABLE_FIELDS_DEF) o[f.key] = user[f.key] || '';
    return o;
  }, [user]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    // Only send fields that changed and are non-empty.
    const payload = {};
    for (const f of EDITABLE_FIELDS_DEF) {
      const cur = (user[f.key] || '');
      const next = (form[f.key] || '').trim();
      if (next && next !== cur) payload[f.key] = next;
    }
    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save.');
      return;
    }
    if (!window.confirm(`Save ${Object.keys(payload).length} field change(s) to ${user.email}?`)) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/accounts/${user.user_id}`, payload);
      const diffs = data?.diffs ? Object.keys(data.diffs).length : 0;
      toast.success(diffs ? `Saved ${diffs} change(s).` : 'No changes applied.');
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-tools-edit-modal">
      <div className="bg-[hsl(var(--card))] rounded-2xl max-w-2xl w-full p-6 my-8 border border-[hsl(var(--primary)/0.18)] shadow-xl">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="ml-overline text-[hsl(var(--accent))]">Edit account</p>
            <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">{user.name || user.email}</h3>
            <p className="text-xs text-[hsl(var(--primary)/0.6)] mt-1 font-mono">{user.user_id}</p>
          </div>
          <button type="button" onClick={onClose} className="text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] text-sm" data-testid="admin-tools-edit-close">
            Close ✕
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {EDITABLE_FIELDS_DEF.map((f) => (
            <label key={f.key} className="block">
              <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium mb-1">{f.label}</div>
              <input
                type="text"
                value={form[f.key] || ''}
                onChange={(e) => set(f.key, e.target.value)}
                className="w-full text-sm bg-transparent border-b border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none px-1 py-1.5"
                data-testid={`admin-tools-edit-${f.key}`}
              />
            </label>
          ))}
        </div>
        <div className="mt-7 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-full border border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary))] text-sm"
            data-testid="admin-tools-edit-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium hover:opacity-90 transition disabled:opacity-60"
            data-testid="admin-tools-edit-save"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TerminateSuspendModal({ user, onClose, onSaved }) {
  const current = user.partner_status || 'pending';
  // iter94 — destructive flow narrowed to the only two states an admin
  // routinely needs from this card: Suspend (temporary, can be reversed
  // via Approve from elsewhere) or Cancel/Terminate (permanent).
  const ACTIONS = [
    { id: 'suspended', label: 'Suspend partner', helper: 'Temporarily pause this partner. They keep their account and can be re-approved later. Their referral code stops attributing new families.' },
    { id: 'cancelled', label: 'Cancel / terminate partner', helper: 'Permanently end this partner relationship. Their referral code stops attributing new families and any active override is closed.' },
  ];
  const [choice, setChoice] = useState(current === 'suspended' ? 'cancelled' : 'suspended');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const selected = ACTIONS.find((a) => a.id === choice) || ACTIONS[0];
  const isNoChange = choice === current;

  const handleSubmit = async () => {
    if (isNoChange) {
      toast.info(`Partner is already "${current}".`);
      return;
    }
    if (!window.confirm(`Confirm: change ${user.email} from "${current}" to "${choice}"?\n\nThis is recorded in the audit log.`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/admin/accounts/${user.user_id}/status`,
        { partner_status: choice, reason: reason.trim() || null },
      );
      if (data?.no_change) toast.info('No change applied.');
      else toast.success(`Partner ${data.to || choice} (was ${data.from || current}).`);
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="admin-tools-terminate-suspend-modal"
    >
      <div className="bg-[hsl(var(--card))] rounded-2xl max-w-lg w-full p-6 my-8 border border-[hsl(var(--primary)/0.18)] shadow-xl">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="ml-overline text-[hsl(var(--destructive,0_70%_40%))]">Partner status</p>
            <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">Terminate or suspend</h3>
            <p className="text-xs text-[hsl(var(--primary)/0.65)] mt-2">
              {user.name || user.email} · current status:{' '}
              <span className="font-medium text-[hsl(var(--primary))]">{current}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] text-sm"
            data-testid="admin-tools-terminate-suspend-close"
          >
            Close ✕
          </button>
        </div>

        <label className="block mb-4">
          <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.6)] font-medium mb-2">Action</div>
          <select
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none rounded-md px-3 py-2.5 text-sm text-[hsl(var(--primary))]"
            data-testid="admin-tools-terminate-suspend-action"
          >
            {ACTIONS.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
          <p className="text-xs text-[hsl(var(--primary)/0.7)] mt-2 leading-relaxed">{selected.helper}</p>
        </label>

        <label className="block mb-2">
          <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.6)] font-medium mb-2">Reason (internal, optional)</div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g., No response to outreach for 60 days · Compliance · Partner request"
            className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none rounded-md px-3 py-2 text-sm text-[hsl(var(--primary))] resize-y"
            data-testid="admin-tools-terminate-suspend-reason"
          />
        </label>

        {isNoChange && (
          <p className="text-xs text-[hsl(var(--accent))] mt-3" data-testid="admin-tools-terminate-suspend-no-change">
            Partner is already "{current}". Pick the other action or close this modal.
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-full border border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary))] text-sm"
            data-testid="admin-tools-terminate-suspend-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || isNoChange}
            className="px-5 py-2.5 rounded-full bg-[hsl(var(--destructive,0_70%_40%))] text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
            data-testid="admin-tools-terminate-suspend-submit"
          >
            {saving ? 'Saving…' : (choice === 'suspended' ? 'Suspend partner' : 'Cancel / terminate partner')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <h2 className="font-serif text-2xl text-[hsl(var(--primary))] mb-3">{title}</h2>
      {children}
    </section>
  );
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 bg-[hsl(var(--card))] border border-[hsl(var(--primary)/0.12)] rounded-2xl p-5">{children}</div>; }
function KV({ label, value, mono }) {
  return (
    <div>
      <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">{label}</div>
      <div className={`text-[hsl(var(--primary))] mt-1 ${mono ? 'font-mono break-all text-xs' : 'text-sm'}`}>{value ?? '—'}</div>
    </div>
  );
}
function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-[hsl(var(--primary)/0.12)]">
      <table className="w-full text-sm">
        <thead className="bg-[hsl(var(--primary)/0.04)] text-[hsl(var(--primary)/0.7)] uppercase tracking-[0.16em] text-[10px]">
          <tr>{headers.map((h) => <th key={h} className="text-left py-2 px-3">{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
function Empty({ children }) {
  return <div className="text-[hsl(var(--primary)/0.55)] text-sm bg-[hsl(var(--card))] border border-[hsl(var(--primary)/0.12)] rounded-2xl px-5 py-6">{children}</div>;
}
